import { visualizationApi } from "@/Api/Visualization";
import { updateStylesInViewer } from "@/Core/Styling/StyleManager";
/**
 * Coalesces overlapping async task executions. When `request()` is called
 * while the task is already running, the task will re-run exactly once after
 * the current execution completes. Multiple `request()` calls during a single
 * execution collapse into one trailing re-run, ensuring no work is lost
 * without unbounded concurrency.
 *
 * Use this for async work (e.g., re-rendering a scene, syncing state) where
 * overlapping executions would be wasteful or incorrect.
 */
export class AsyncTaskCoalescer {
    _running = false;
    _pendingRerun = false;
    _task;
    /**
     * @param {() => Promise<void>} task         The async function to coalesce.
     * @param {boolean}             [immediate]  If true, the task is executed
     *                                           once immediately upon
     *                                           construction, before any
     *                                           explicit `request()` call.
     *                                           Default is false.
     */
    constructor(task, immediate = false) {
        this._task = task;
        if (immediate) {
            this._run();
        }
    }
    /**
     * Request the task to run. If the task is idle, it starts immediately.
     * If already running, a single re-run is queued for after completion.
     */
    request() {
        if (this._running) {
            this._pendingRerun = true;
            return;
        }
        this._run();
    }
    /**
     * Internal method to execute the task. Manages the running state and
     * pending re-run flag to ensure correct coalescing behavior.
     *
     * @returns {Promise<void>} Resolves when the task (and any immediate
     *     re-run) completes.
     */
    async _run() {
        this._running = true;
        this._pendingRerun = false;
        try {
            await this._task();
        }
        finally {
            this._running = false;
            if (this._pendingRerun) {
                this._pendingRerun = false;
                this._run();
            }
        }
    }
}
/**
 * Creates a leading-edge throttle: fires immediately on first call,
 * then suppresses for delayMs. A trailing call is scheduled at the
 * end of the suppression window.
 *
 * @param {() => void} fn        The function to throttle.
 * @param {number}     delayMs   The suppression window in milliseconds.
 * @returns {{ invoke: () => void; cancel: () => void }}  Control object.
 */
export function createLeadingEdgeThrottle(fn, delayMs) {
    let timer = null;
    let lastInvoked = 0;
    return {
        /**
         * Invoke the throttled function. If the cooldown period has passed
         * since the last invocation, the function is called immediately.
         * Otherwise, the call is suppressed but a timer is (re)started to
         * ensure a trailing call occurs after the cooldown.
         */
        invoke() {
            const now = Date.now();
            const elapsed = now - lastInvoked;
            if (timer !== null) {
                clearTimeout(timer);
                timer = null;
            }
            if (elapsed >= delayMs) {
                // Enough time has passed; fire immediately.
                lastInvoked = now;
                fn();
            }
            // Always (re)start the cooldown timer. If we just fired,
            // this prevents another immediate call. If we suppressed,
            // this keeps the window open until calls stop.
            timer = setTimeout(() => {
                timer = null;
                lastInvoked = Date.now();
            }, delayMs);
        },
        /**
         * Cancel any pending trailing call and reset the throttle state.
         */
        cancel() {
            if (timer !== null) {
                clearTimeout(timer);
                timer = null;
            }
        },
    };
}
/**
 * Coalesces calls into a single requestAnimationFrame callback.
 *
 * @param {() => void} fn  The function to call on the next animation frame.
 * @returns {{ invoke: () => void; cancel: () => void }}  Control object.
 */
export function createAnimationFrameCoalescer(fn) {
    let frameId = null;
    return {
        /**
         * Request the function to be called on the next animation frame. If
         * already requested and waiting, does nothing (coalesces into the
         * pending call).
         */
        invoke() {
            if (frameId !== null) {
                cancelAnimationFrame(frameId);
            }
            frameId = requestAnimationFrame(() => {
                frameId = null;
                fn();
            });
        },
        /**
         * Cancel any pending animation frame callback.
         */
        cancel() {
            if (frameId !== null) {
                cancelAnimationFrame(frameId);
                frameId = null;
            }
        },
    };
}
/**
 * Debounces a function so it only fires after a quiet period with no
 * calls. Unlike leading-edge throttle, this only fires on the trailing
 * edge.
 *
 * @param {() => void} fn       The function to debounce.
 * @param {number}     delayMs  The quiet period in milliseconds.
 * @returns {{ invoke: () => void; cancel: () => void }}  Control object.
 */
export function createTrailingEdgeDebounce(fn, delayMs) {
    let timer = null;
    return {
        /**
         * Invoke the debounced function. If called again within the quiet
         * period, the timer resets. The function only fires after calls have
         * stopped for delayMs.
         */
        invoke() {
            if (timer !== null) {
                clearTimeout(timer);
            }
            timer = setTimeout(() => {
                timer = null;
                fn();
            }, delayMs);
        },
        /**
         * Cancel any pending call and reset the debounce state.
         */
        cancel() {
            if (timer !== null) {
                clearTimeout(timer);
                timer = null;
            }
        },
    };
}
/**
 * Whether visualization updates are currently deferred. Checked by
 * ViewerParent.renderAll() and ViewerPanel._updateStyleChanges() to
 * skip expensive canvas repaints while molecules are being batch-loaded.
 */
let _visualizationDeferred = false;
/**
 * Returns true if visualization is currently deferred via
 * deferVisualization().
 *
 * @returns {boolean}  Whether rendering is suppressed.
 */
export function isVisualizationDeferred() {
    return _visualizationDeferred;
}
/**
 * Run a callback with viewer rendering suppressed. While the callback
 * executes, all renderAll() calls and style-update render passes are
 * skipped. After the callback completes (or throws), the flag is
 * cleared and a single forced render is triggered so that all
 * accumulated changes appear at once.
 *
 * @param {() => Promise<void>} fn  The async work to perform while
 *     visualization is deferred (e.g., loading multiple molecules).
 * @returns {Promise<void>}
 */
export async function deferVisualization(fn) {
    _visualizationDeferred = true;
    try {
        await fn();
    }
    finally {
        _visualizationDeferred = false;
        // Lazy import to avoid circular dependency at module load time.
        // const { visualizationApi } = await i_mport("@/Api/Visualization");
        // const { updateStylesInViewer } = await i_mport("@/Core/Styling/StyleManager");
        updateStylesInViewer();
        const viewer = await visualizationApi.viewer;
        await viewer.renderImmediate();
        viewer.zoomOnFocused();
    }
}
